import { useCallback, useMemo } from 'react';
import { DEFAULT_SOUNDBITE_PRESETS, SOUNDBITE_PACKS, isSoundbiteOwned } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { BottomSheet } from './BottomSheet';

interface SoundbiteSheetProps {
  open: boolean;
  onClose: () => void;
  onToggleSoundbite: (soundbiteId: string) => void;
  soundbiteCooldown: boolean;
  playingSoundbite: string | null;
}

const USAGE_KEY = 'flip7_soundbite_usage';
const MOST_USED_COUNT = 4;

function getUsage(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(USAGE_KEY) || '{}');
  } catch { return {}; }
}

function trackSoundbiteUsage(soundbiteId: string) {
  const usage = getUsage();
  usage[soundbiteId] = (usage[soundbiteId] ?? 0) + 1;
  localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
}

const soundbiteEntries = Object.entries(DEFAULT_SOUNDBITE_PRESETS);

export function SoundbiteSheet({
  open,
  onClose,
  onToggleSoundbite,
  soundbiteCooldown,
  playingSoundbite,
}: SoundbiteSheetProps) {
  const { user } = useAuth();
  const inventory = useMemo(() => user?.inventory ?? [], [user?.inventory]);
  const achievements = user?.achievements;
  const usage = useMemo(() => (open ? getUsage() : {}), [open]);

  const mostUsed = useMemo(() => {
    const entries = soundbiteEntries
      .filter(([id, preset]) => {
        const count = usage[id] ?? 0;
        return count > 0 && isSoundbiteOwned(id, preset, inventory, achievements);
      })
      .sort((a, b) => (usage[b[0]] ?? 0) - (usage[a[0]] ?? 0))
      .slice(0, MOST_USED_COUNT);
    return entries;
  }, [usage, inventory, achievements]);

  const packGroups = useMemo(() => {
    const groups: Record<string, [string, typeof DEFAULT_SOUNDBITE_PRESETS[string]][]> = {};
    for (const pack of SOUNDBITE_PACKS) {
      groups[pack] = soundbiteEntries.filter(([, preset]) => preset.pack === pack);
    }
    return groups;
  }, []);

  const handlePlay = useCallback((id: string) => {
    trackSoundbiteUsage(id);
    onToggleSoundbite(id);
  }, [onToggleSoundbite]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Soundbites">
      <div className="soundbite-sheet">
        {/* Most Used section */}
        {mostUsed.length > 0 && (
          <div className="soundbite-sheet__section">
            <h3 className="soundbite-sheet__heading">Most Used</h3>
            <div className="soundbite-sheet__grid">
              {mostUsed.map(([id, preset]) => {
                const isPlaying = playingSoundbite === id;
                return (
                  <button
                    key={id}
                    className={`soundbite-sheet__btn${isPlaying ? ' soundbite-sheet__btn--playing' : ''}`}
                    onClick={() => isPlaying ? onToggleSoundbite(id) : handlePlay(id)}
                    disabled={soundbiteCooldown && !isPlaying}
                  >
                    <span className="soundbite-sheet__btn-icon">{isPlaying ? '\u23F9' : preset.icon}</span>
                    <span className="soundbite-sheet__btn-label">{preset.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* By pack sections — ownership is per-pack, checked via first entry */}
        {SOUNDBITE_PACKS.map(pack => {
          const entries = packGroups[pack];
          if (!entries || entries.length === 0) return null;
          const owned = isSoundbiteOwned(entries[0][0], entries[0][1], inventory, achievements);
          return (
            <div key={pack} className="soundbite-sheet__section">
              <h3 className="soundbite-sheet__heading">
                {!owned && <span className="soundbite-sheet__lock-icon">{'\uD83D\uDD12'}</span>}
                {pack}
                {!owned && <span className="soundbite-sheet__unlock-hint">Unlock in Shop</span>}
              </h3>
              {owned ? (
                <div className="soundbite-sheet__grid">
                  {entries.map(([id, preset]) => {
                    const isPlaying = playingSoundbite === id;
                    const isOwned = isSoundbiteOwned(id, preset, inventory, achievements);
                    return (
                      <button
                        key={id}
                        className={`soundbite-sheet__btn${isPlaying ? ' soundbite-sheet__btn--playing' : ''}${!isOwned ? ' soundbite-sheet__btn--locked' : ''}`}
                        onClick={() => {
                          if (!isOwned) return;
                          if (isPlaying) { onToggleSoundbite(id); return; }
                          handlePlay(id);
                        }}
                        disabled={!isOwned || (soundbiteCooldown && !isPlaying)}
                        title={!isOwned ? 'Unlock in Shop' : undefined}
                      >
                        <span className="soundbite-sheet__btn-icon">{isPlaying ? '\u23F9' : !isOwned ? '\uD83D\uDD12' : preset.icon}</span>
                        <span className="soundbite-sheet__btn-label">{preset.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="soundbite-sheet__locked-msg">
                  {entries.length} soundbites
                </p>
              )}
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}
